/**
 * OrganizationSettings — three stacked cards for the Settings →
 * Organization tab:
 *   • OrgGeneralCard  — name, plan, Sentinel Guardian, member count
 *   • OrgMembersCard  — list + invite (admin) + role/remove (admin)
 *   • OrgGroupsCard   — list + create / edit / delete
 *
 * Self-contained: pulls all data from `useOrganization()`. The parent
 * (SettingsPage) decides whether to render this tab at all (only when
 * `hasOrg === true`).
 */
import React, { useMemo, useState } from 'react';
import { X, Plus, Loader2, ChevronDown, Mail } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { useOrganization } from '@/hooks/useOrganization';
import { useAuth } from '@/contexts/AuthContext';

// ── Tiny shared primitives ──────────────────────────────────────────
const Card = ({ children, testId }) => (
  <section
    data-testid={testId}
    className="border border-[#E5E1D8] rounded-lg bg-white p-6"
  >
    {children}
  </section>
);

const CardHeader = ({ children }) => (
  <header className="flex items-center justify-between mb-4">{children}</header>
);

const CardTitle = ({ children }) => (
  <h2 className="text-[15px] font-semibold text-[#111111]">{children}</h2>
);

const Field = ({ label, children }) => (
  <div className="flex items-start justify-between py-2.5 border-b border-[#F0EDE5] last:border-0">
    <span className="text-[13px] text-[#666666]">{label}</span>
    <span className="text-[13.5px] text-[#111111] text-right">{children}</span>
  </div>
);

const Badge = ({ color = 'gray', children, testId }) => {
  const palette = {
    gray:  'bg-[#F0EDE5] text-[#666666] border-[#E5E1D8]',
    green: 'bg-[#E7F4EB] text-[#137A3E] border-[#C7E6D0]',
    black: 'bg-[#111111] text-white border-[#111111]',
    blue:  'bg-[#E6F4FF] text-[#005B9E] border-[#BFE0FF]',
  }[color] || 'bg-[#F0EDE5] text-[#666666] border-[#E5E1D8]';
  return (
    <span
      data-testid={testId}
      className={cn('inline-flex items-center gap-1 px-2 py-0.5 text-[11.5px] rounded-full border', palette)}
    >
      {children}
    </span>
  );
};

const initialsOf = (name = '') => name
  .trim()
  .split(/\s+/)
  .slice(0, 2)
  .map((p) => p[0]?.toUpperCase() || '')
  .join('') || 'B';

const toHandle = (name) =>
  (name || '')
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[^a-z0-9\u0600-\u06FF]+/g, '-')
    .replace(/^-+|-+$/g, '') || 'group';

// ── OrganizationSettings (root) ─────────────────────────────────────
export default function OrganizationSettings({ isArabic = false, t = (en) => en }) {
  const { user } = useAuth();
  const myUserId = user?.id;
  const org = useOrganization();
  const role = org.member?.role;

  if (org.loading) {
    return (
      <div className="flex items-center gap-2 text-[#666666] text-[13px]" data-testid="org-settings-loading">
        <Loader2 className="w-4 h-4 animate-spin" /> {t('Loading organization…', 'جارٍ تحميل المنظمة…')}
      </div>
    );
  }
  if (!org.hasOrg) {
    return (
      <Card testId="org-settings-empty">
        <p className="text-[13px] text-[#666666]">
          {t('You do not belong to an organization yet.', 'أنت لا تنتمي إلى منظمة بعد.')}
        </p>
      </Card>
    );
  }

  return (
    <div className="space-y-5" data-testid="organization-settings">
      <OrgGeneralCard org={org.org} memberCount={org.members?.length || 0} t={t} />
      <OrgMembersCard
        members={org.members || []}
        currentUserId={myUserId}
        role={role}
        invite={org.inviteMember}
        updateRole={org.updateMemberRole}
        remove={org.removeMember}
        t={t}
      />
      <OrgGroupsCard
        groups={org.groups || []}
        members={org.members || []}
        role={role}
        createGroup={org.createGroup}
        updateGroup={org.updateGroup}
        deleteGroup={org.deleteGroup}
        t={t}
      />
    </div>
  );
}

// ── OrgGeneralCard ──────────────────────────────────────────────────
function OrgGeneralCard({ org, memberCount, t }) {
  const planLabel = {
    starter:        t('Starter',          'مبتدئ'),
    professional:   t('Professional',     'احترافي'),
    enterprise_plus:t('Enterprise Plus',  'مؤسسي بلس'),
  }[org?.plan] || org?.plan;
  return (
    <Card testId="org-general-card">
      <CardHeader><CardTitle>{t('Organization', 'المنظمة')}</CardTitle></CardHeader>
      <Field label={t('Name', 'الاسم')}>
        <span data-testid="org-general-name">{org?.name}</span>
      </Field>
      <Field label={t('Plan', 'الخطة')}>
        <Badge color="black" testId="org-general-plan">{planLabel}</Badge>
      </Field>
      <Field label={t('Sentinel Guardian', 'حارس Sentinel')}>
        {org?.sentinel_guardian ? (
          <Badge color="green" testId="org-general-guardian-on">{t('Enabled', 'مفعّل')}</Badge>
        ) : (
          <span data-testid="org-general-guardian-off">
            <Badge color="gray">{t('Not enabled', 'غير مفعّل')}</Badge>{' '}
            <a href="mailto:sales@bayenah.com" className="text-[12px] text-[#005B9E] underline">
              {t('Contact sales', 'تواصل مع المبيعات')}
            </a>
          </span>
        )}
      </Field>
      <Field label={t('Members', 'الأعضاء')}>
        <span data-testid="org-general-member-count">
          {memberCount} {t(memberCount === 1 ? 'member' : 'members', 'عضو')}
        </span>
      </Field>
    </Card>
  );
}

// ── OrgMembersCard ──────────────────────────────────────────────────
function OrgMembersCard({ members, currentUserId, role, invite, updateRole, remove, t }) {
  const [inviteOpen, setInviteOpen] = useState(false);
  const isAdmin = role === 'admin';

  return (
    <Card testId="org-members-card">
      <CardHeader>
        <CardTitle>{t('Members', 'الأعضاء')}</CardTitle>
        {isAdmin && (
          <button
            type="button"
            onClick={() => setInviteOpen(true)}
            data-testid="org-invite-button"
            className="inline-flex items-center gap-1.5 rounded-full bg-[#111111] text-white text-[12.5px] font-medium px-3.5 py-1.5 hover:bg-black/85"
          >
            <Plus className="w-3.5 h-3.5" /> {t('+ Invite', '+ دعوة')}
          </button>
        )}
      </CardHeader>

      {members.length === 0 ? (
        <p className="text-[13px] text-[#666666]" data-testid="org-members-empty">
          {t('No members yet.', 'لا يوجد أعضاء بعد.')}
        </p>
      ) : (
        <ul className="divide-y divide-[#F0EDE5]" data-testid="org-members-list">
          {members.map((m) => (
            <li
              key={m.user_id}
              data-testid={`org-member-row-${m.user_id}`}
              className="flex items-center gap-3 py-3"
            >
              <div className="w-9 h-9 rounded-full bg-[#F0EDE5] text-[#111111] flex items-center justify-center text-[12.5px] font-medium">
                {initialsOf(m.display_name || m.email)}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-[13.5px] font-medium text-[#111111] truncate">
                    {m.display_name || m.email}
                  </span>
                  {m.user_id === currentUserId && (
                    <Badge color="blue" testId="org-member-you-badge">{t('You', 'أنت')}</Badge>
                  )}
                </div>
                <div className="text-[12px] text-[#666666] truncate">{m.email}</div>
              </div>
              <span className="text-[12px] text-[#666666] capitalize w-20 text-right hidden sm:inline">
                {t(m.role, m.role)}
              </span>
              <Badge color={m.status === 'active' ? 'green' : 'gray'}>
                {m.status === 'active' ? t('Active', 'نشط') : t('Pending', 'بانتظار')}
              </Badge>
              {isAdmin && m.user_id !== currentUserId && (
                <MemberAdminActions m={m} updateRole={updateRole} remove={remove} t={t} />
              )}
            </li>
          ))}
        </ul>
      )}

      <InviteDrawer
        open={inviteOpen}
        onClose={() => setInviteOpen(false)}
        invite={invite}
        t={t}
      />
    </Card>
  );
}

function MemberAdminActions({ m, updateRole, remove, t }) {
  const [busy, setBusy] = useState(false);
  // Cluster C-6 (Feb 06 2026): wrap raw backend error strings with a
  // localized fallback. We keep the raw message when the UI is EN
  // (preserves diagnostic signal for admin users) and substitute a
  // generic AR sentence otherwise — same pattern as HeraldPanel.
  const errToast = (err, fallback_en, fallback_ar) => {
    const raw = String(err?.message || err || '');
    toast.error(t(raw || fallback_en, fallback_ar));
  };
  const onRoleChange = async (e) => {
    const newRole = e.target.value;
    if (newRole === m.role) return;
    setBusy(true);
    try { await updateRole(m.user_id, newRole); toast.success(t('Role updated', 'تم تحديث الدور')); }
    catch (err) { errToast(err, 'Update failed', 'فشل التحديث'); }
    finally { setBusy(false); }
  };
  const onRemove = async () => {
    // eslint-disable-next-line no-restricted-globals, no-alert
    if (!confirm(t(`Remove ${m.display_name || m.email}?`, `إزالة ${m.display_name || m.email}؟`))) return;
    setBusy(true);
    try { await remove(m.user_id); toast.success(t('Member removed', 'تمت إزالة العضو')); }
    catch (err) { errToast(err, 'Remove failed', 'فشل الحذف'); }
    finally { setBusy(false); }
  };
  return (
    <div className="flex items-center gap-2" data-testid={`org-member-actions-${m.user_id}`}>
      <select
        value={m.role}
        onChange={onRoleChange}
        disabled={busy}
        data-testid={`org-member-role-select-${m.user_id}`}
        className="text-[12px] border border-[#E5E1D8] rounded px-1.5 py-1 bg-white"
      >
        <option value="admin">{t('admin', 'مسؤول')}</option>
        <option value="manager">{t('manager', 'مدير')}</option>
        <option value="member">{t('member', 'عضو')}</option>
      </select>
      <button
        type="button"
        onClick={onRemove}
        disabled={busy}
        data-testid={`org-member-remove-${m.user_id}`}
        className="text-[12px] text-red-700 hover:underline disabled:opacity-50"
      >
        {t('Remove', 'إزالة')}
      </button>
    </div>
  );
}

// ── InviteDrawer ────────────────────────────────────────────────────
function InviteDrawer({ open, onClose, invite, t }) {
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('member');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  if (!open) return null;
  const submit = async () => {
    if (!email || sending) return;
    setSending(true); setSent(false);
    try { await invite(email, role); setSent(true); setEmail(''); toast.success(t('Invitation sent', 'تم إرسال الدعوة')); }
    catch (err) {
      // Cluster C-6: localize fallback when raw backend message is absent.
      const raw = String(err?.message || err || '');
      toast.error(t(raw || 'Invite failed', 'فشل إرسال الدعوة'));
    }
    finally { setSending(false); }
  };
  return (
    <div className="fixed inset-0 z-50" data-testid="org-invite-drawer">
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />
      <aside className="absolute top-0 right-0 h-full w-full sm:w-[420px] bg-white border-l border-[#E5E1D8] p-6 overflow-y-auto">
        <header className="flex items-center justify-between mb-5">
          <h3 className="text-[16px] font-semibold text-[#111111]">
            {t('Invite a team member', 'دعوة عضو جديد')}
          </h3>
          <button type="button" onClick={onClose} aria-label="Close" data-testid="org-invite-close">
            <X className="w-4 h-4" />
          </button>
        </header>

        <label className="block">
          <span className="text-[12.5px] text-[#666666]">{t('Email address', 'البريد الإلكتروني')}</span>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="colleague@company.com"
            data-testid="org-invite-email"
            className="mt-1 w-full border border-[#E5E1D8] rounded-md px-3 py-2 text-[13px]"
          />
        </label>

        <label className="block mt-4">
          <span className="text-[12.5px] text-[#666666]">{t('Role', 'الدور')}</span>
          <select
            value={role}
            onChange={(e) => setRole(e.target.value)}
            data-testid="org-invite-role"
            className="mt-1 w-full border border-[#E5E1D8] rounded-md px-3 py-2 text-[13px] bg-white"
          >
            <option value="member">{t('Member', 'عضو')}</option>
            <option value="manager">{t('Manager', 'مدير')}</option>
            <option value="admin">{t('Admin', 'مسؤول')}</option>
          </select>
        </label>

        <div className="mt-4 text-[11.5px] text-[#666666] space-y-1" data-testid="org-invite-role-explainer">
          <p><strong>{t('Member', 'عضو')}</strong> — {t('can use all features, cannot manage team', 'يمكنه استخدام كل الميزات، لا يدير الفريق')}</p>
          <p><strong>{t('Manager', 'مدير')}</strong> — {t('can view team, cannot invite or remove', 'يستطيع رؤية الفريق دون الدعوة أو الإزالة')}</p>
          <p><strong>{t('Admin', 'مسؤول')}</strong> — {t('full access including team management', 'وصول كامل بما في ذلك إدارة الفريق')}</p>
        </div>

        <button
          type="button"
          onClick={submit}
          disabled={!email || sending}
          data-testid="org-invite-send"
          className="mt-5 w-full inline-flex items-center justify-center gap-1.5 rounded-full bg-[#111111] text-white text-[13.5px] font-medium px-4 py-2.5 hover:bg-black/85 disabled:opacity-40"
        >
          {sending
            ? <><Loader2 className="w-4 h-4 animate-spin" /> {t('Sending…', 'جاري الإرسال…')}</>
            : <><Mail className="w-3.5 h-3.5" /> {t('Send invitation', 'إرسال الدعوة')}</>}
        </button>

        {sent && (
          <p className="mt-3 text-[12.5px] text-green-700" data-testid="org-invite-success">
            {t('Invitation sent', 'تم إرسال الدعوة')}
          </p>
        )}
      </aside>
    </div>
  );
}

// ── OrgGroupsCard ───────────────────────────────────────────────────
function OrgGroupsCard({ groups, members, role, createGroup, updateGroup, deleteGroup, t }) {
  const [drawer, setDrawer] = useState({ open: false, editing: null });
  const isAdmin = role === 'admin';

  const onDelete = async (group) => {
    // eslint-disable-next-line no-restricted-globals, no-alert
    if (!confirm(t(`Delete group "${group.name}"?`, `حذف المجموعة "${group.name}"؟`))) return;
    try { await deleteGroup(group.id); toast.success(t('Group deleted', 'تم حذف المجموعة')); }
    catch (err) {
      // Cluster C-6: localize fallback.
      const raw = String(err?.message || err || '');
      toast.error(t(raw || 'Delete failed', 'فشل الحذف'));
    }
  };

  return (
    <Card testId="org-groups-card">
      <CardHeader>
        <CardTitle>{t('Groups', 'المجموعات')}</CardTitle>
        <button
          type="button"
          onClick={() => setDrawer({ open: true, editing: null })}
          data-testid="org-create-group-button"
          className="inline-flex items-center gap-1.5 rounded-full bg-[#111111] text-white text-[12.5px] font-medium px-3.5 py-1.5 hover:bg-black/85"
        >
          <Plus className="w-3.5 h-3.5" /> {t('+ Create group', '+ إنشاء مجموعة')}
        </button>
      </CardHeader>

      {groups.length === 0 ? (
        <p className="text-[13px] text-[#666666]" data-testid="org-groups-empty">
          {t(
            'No groups yet. Create groups to mention multiple people at once with Herald.',
            'لا توجد مجموعات بعد. أنشئ مجموعات لتذكر عدة أشخاص دفعة واحدة عبر Herald.'
          )}
        </p>
      ) : (
        <ul className="divide-y divide-[#F0EDE5]" data-testid="org-groups-list">
          {groups.map((g) => (
            <li
              key={g.id}
              data-testid={`org-group-row-${g.id}`}
              className="flex items-center gap-3 py-3"
            >
              <span className="text-[12px] text-[#005B9E] font-mono">@{g.handle}</span>
              <span className="text-[13.5px] font-medium text-[#111111] flex-1 truncate">
                {g.name}
              </span>
              <span className="text-[12px] text-[#666666]">
                {(g.member_ids || []).length} {t('members', 'أعضاء')}
              </span>
              <button
                type="button"
                onClick={() => setDrawer({ open: true, editing: g })}
                data-testid={`org-group-edit-${g.id}`}
                className="text-[12px] text-[#111111] hover:underline"
              >
                {t('Edit', 'تعديل')}
              </button>
              {isAdmin && (
                <button
                  type="button"
                  onClick={() => onDelete(g)}
                  data-testid={`org-group-delete-${g.id}`}
                  className="text-[12px] text-red-700 hover:underline"
                >
                  {t('Delete', 'حذف')}
                </button>
              )}
            </li>
          ))}
        </ul>
      )}

      <GroupDrawer
        open={drawer.open}
        editing={drawer.editing}
        members={members}
        onClose={() => setDrawer({ open: false, editing: null })}
        createGroup={createGroup}
        updateGroup={updateGroup}
        t={t}
      />
    </Card>
  );
}

function GroupDrawer({ open, editing, members, onClose, createGroup, updateGroup, t }) {
  const [name, setName] = useState('');
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [saving, setSaving] = useState(false);

  // Reset on (re-)open
  React.useEffect(() => {
    if (open) {
      setName(editing?.name || '');
      setSelectedIds(new Set(editing?.member_ids || []));
    }
  }, [open, editing]);

  const active = useMemo(() => members.filter((m) => m.status === 'active'), [members]);

  if (!open) return null;
  const toggle = (uid) => {
    const next = new Set(selectedIds);
    if (next.has(uid)) next.delete(uid); else next.add(uid);
    setSelectedIds(next);
  };
  const submit = async () => {
    if (!name || selectedIds.size === 0 || saving) return;
    setSaving(true);
    try {
      if (editing) {
        await updateGroup(editing.id, { name, memberIds: Array.from(selectedIds) });
        toast.success(t('Group updated', 'تم تحديث المجموعة'));
      } else {
        await createGroup(name, Array.from(selectedIds));
        toast.success(t('Group created', 'تم إنشاء المجموعة'));
      }
      onClose();
    } catch (err) {
      // Cluster C-6: localize fallback.
      const raw = String(err?.message || err || '');
      toast.error(t(raw || 'Save failed', 'فشل الحفظ'));
    }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50" data-testid="org-group-drawer">
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />
      <aside className="absolute top-0 right-0 h-full w-full sm:w-[440px] bg-white border-l border-[#E5E1D8] p-6 overflow-y-auto">
        <header className="flex items-center justify-between mb-5">
          <h3 className="text-[16px] font-semibold text-[#111111]">
            {editing ? t('Edit group', 'تعديل المجموعة') : t('Create group', 'إنشاء مجموعة')}
          </h3>
          <button type="button" onClick={onClose} aria-label="Close" data-testid="org-group-drawer-close">
            <X className="w-4 h-4" />
          </button>
        </header>

        <label className="block">
          <span className="text-[12.5px] text-[#666666]">{t('Group name', 'اسم المجموعة')}</span>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={t('Legal Team', 'الفريق القانوني')}
            data-testid="org-group-name"
            className="mt-1 w-full border border-[#E5E1D8] rounded-md px-3 py-2 text-[13px]"
          />
          {name && (
            <span className="text-[11.5px] text-[#005B9E] font-mono mt-1 inline-block" data-testid="org-group-handle-preview">
              @{toHandle(name)}
            </span>
          )}
        </label>

        <div className="mt-5">
          <span className="text-[12.5px] text-[#666666]">{t('Members', 'الأعضاء')}</span>
          <ul className="mt-1 max-h-72 overflow-y-auto border border-[#E5E1D8] rounded-md p-2 space-y-1" data-testid="org-group-member-list">
            {active.map((m) => (
              <li
                key={m.user_id}
                className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-[#FAFAF8] cursor-pointer"
                onClick={() => toggle(m.user_id)}
              >
                <input
                  type="checkbox"
                  checked={selectedIds.has(m.user_id)}
                  onChange={() => toggle(m.user_id)}
                  data-testid={`org-group-member-${m.user_id}`}
                />
                <span className="text-[13px] text-[#111111] flex-1 truncate">{m.display_name || m.email}</span>
                <span className="text-[11.5px] text-[#666666] truncate">{m.email}</span>
              </li>
            ))}
          </ul>
        </div>

        <button
          type="button"
          onClick={submit}
          disabled={!name || selectedIds.size === 0 || saving}
          data-testid="org-group-save"
          className="mt-5 w-full inline-flex items-center justify-center gap-1.5 rounded-full bg-[#111111] text-white text-[13.5px] font-medium px-4 py-2.5 hover:bg-black/85 disabled:opacity-40"
        >
          {saving
            ? <><Loader2 className="w-4 h-4 animate-spin" /> {t('Saving…', 'جاري الحفظ…')}</>
            : (editing ? t('Save changes', 'حفظ التغييرات') : t('Create group', 'إنشاء المجموعة'))}
        </button>
      </aside>
    </div>
  );
}
